import { Injectable, signal, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, Observable, fromEvent } from 'rxjs';
// LOW-003: Removed unused 'merge' import
import { filter, map, debounceTime } from 'rxjs/operators';
import { LoggerService } from '@core/services/logger.service';
import { AppStorageService } from '@core/services/app-storage.service';

/**
 * Configuration constants for cross-tab authentication
 * LOW-002: Extract magic numbers to named constants
 */
const CROSS_TAB_CONFIG = {
  /** Delay before cleaning up localStorage message (ms) */
  MESSAGE_CLEANUP_DELAY_MS: 100,
  /** Maximum age for accepting messages (ms) */
  MESSAGE_MAX_AGE_MS: 5000,
  /** Debounce time for focus events (ms) */
  FOCUS_DEBOUNCE_MS: 100,
} as const;

/**
 * Message types for cross-tab authentication events
 */
export type CrossTabAuthMessageType =
  | 'LOGOUT'
  | 'LOGIN'
  | 'TOKEN_REFRESHED'
  | 'SESSION_EXTENDED'
  | 'SESSION_EXPIRED'
  | 'TENANT_SWITCHED';

/**
 * Message structure for cross-tab communication
 * SECURITY: Never include sensitive tokens in the payload.
 * Other tabs should fetch fresh credentials from secure storage.
 */
export interface CrossTabAuthMessage {
  type: CrossTabAuthMessageType;
  timestamp: number;
  tabId: string;
  /** HMAC signature for message integrity verification */
  signature?: string;
  payload?: {
    /** User info (non-sensitive subset only) */
    user?: {
      id: string;
      username: string;
      firstName: string;
      lastName: string;
      roles: string[];
    };
    /** Tenant info for TENANT_SWITCHED events */
    tenant?: {
      id: string;
      name: string;
      code: string;
    };
  };
}

/**
 * Cross-Tab Authentication Synchronization Service
 *
 * Synchronizes authentication state across browser tabs using:
 * - BroadcastChannel API (modern browsers)
 * - localStorage events (fallback for older browsers)
 *
 * Features:
 * - Logout synchronization: Logout in one tab logs out all tabs
 * - Login synchronization: Login in one tab updates all tabs
 * - Token refresh synchronization: Token refresh in one tab updates all tabs
 * - Session expiry notifications: Session expiry in one tab notifies all tabs
 * - Tab focus detection: Sync state when tab regains focus
 * - Unique tab identification: Filter out messages from same tab
 *
 * Usage:
 * ```typescript
 * constructor(private crossTabAuth: CrossTabAuthService) {}
 *
 * ngOnInit() {
 *   this.crossTabAuth.initialize();
 *
 *   // Listen for logout events from other tabs
 *   this.crossTabAuth.logout$.subscribe(() => {
 *     this.authService.logout();
 *   });
 *
 *   // Listen for token refresh events from other tabs
 *   this.crossTabAuth.tokenRefresh$.subscribe(msg => {
 *     this.authService.updateToken(msg.payload?.accessToken);
 *   });
 * }
 *
 * // When user logs out in this tab
 * onLogout() {
 *   this.crossTabAuth.broadcastLogout();
 *   this.authService.logout();
 * }
 * ```
 *
 * DEV-406: Cross-tab authentication synchronization
 */
@Injectable({
  providedIn: 'root'
})
export class CrossTabAuthService {
  private readonly logger = inject(LoggerService).withContext({ component: 'CrossTabAuthService' });
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStorage = inject(AppStorageService);

  // Unique identifier for this tab
  readonly tabId: string;

  // BroadcastChannel for modern browsers
  private channel: BroadcastChannel | null = null;
  private readonly CHANNEL_NAME = 'razor_auth';

  // localStorage key for fallback
  private readonly STORAGE_KEY = 'razor_auth_event';

  // HMAC session key for message integrity (shared via localStorage)
  private readonly HMAC_KEY_STORAGE = 'razor_auth_hmac_key';
  private hmacKey: CryptoKey | null = null;

  // Signal for channel support detection
  readonly isChannelSupported = signal<boolean>(false);

  // Internal subjects for message routing
  private readonly logoutSubject = new Subject<CrossTabAuthMessage>();
  private readonly loginSubject = new Subject<CrossTabAuthMessage>();
  private readonly tokenRefreshSubject = new Subject<CrossTabAuthMessage>();
  private readonly sessionExtendedSubject = new Subject<CrossTabAuthMessage>();
  private readonly sessionExpiredSubject = new Subject<CrossTabAuthMessage>();
  private readonly tenantSwitchSubject = new Subject<CrossTabAuthMessage>();

  // Public observables for consumers
  readonly logout$: Observable<CrossTabAuthMessage> = this.logoutSubject.asObservable();
  readonly login$: Observable<CrossTabAuthMessage> = this.loginSubject.asObservable();
  readonly tokenRefresh$: Observable<CrossTabAuthMessage> = this.tokenRefreshSubject.asObservable();
  readonly sessionExtended$: Observable<CrossTabAuthMessage> = this.sessionExtendedSubject.asObservable();
  readonly sessionExpired$: Observable<CrossTabAuthMessage> = this.sessionExpiredSubject.asObservable();
  readonly tenantSwitch$: Observable<CrossTabAuthMessage> = this.tenantSwitchSubject.asObservable();

  constructor() {
    // Generate unique tab identifier
    this.tabId = this.generateTabId();

    this.logger.debug('[CrossTabAuth] Service initialized', { tabId: this.tabId });
  }

  /**
   * Initialize cross-tab communication
   * Must be called during app initialization (e.g., in APP_INITIALIZER or app.component)
   */
  async initialize(): Promise<void> {
    this.logger.debug('[CrossTabAuth] Initializing cross-tab communication');

    // Initialize HMAC key for message signing
    await this.initializeHmacKey();

    // Check for BroadcastChannel support
    const hasChannelSupport = typeof BroadcastChannel !== 'undefined';
    this.isChannelSupported.set(hasChannelSupport);

    if (hasChannelSupport) {
      this.initializeBroadcastChannel();
    } else {
      this.logger.warn('[CrossTabAuth] BroadcastChannel not supported, using localStorage fallback');
      this.initializeLocalStorageFallback();
    }

    // Listen for tab focus to sync state
    this.setupFocusListener();

    // Clean up on destroy
    this.destroyRef.onDestroy(() => {
      this.destroy();
    });

    this.logger.info('[CrossTabAuth] Cross-tab communication initialized', {
      tabId: this.tabId,
      method: hasChannelSupport ? 'BroadcastChannel' : 'localStorage',
      hmacEnabled: this.hmacKey !== null
    });
  }

  /**
   * Broadcast logout event to all other tabs
   * Rotates HMAC key after broadcasting to invalidate old messages
   */
  async broadcastLogout(): Promise<void> {
    this.logger.debug('[CrossTabAuth] Broadcasting logout event', { tabId: this.tabId });
    await this.broadcast({
      type: 'LOGOUT',
      timestamp: Date.now(),
      tabId: this.tabId
    });

    // Rotate HMAC key after broadcasting to invalidate old messages
    await this.rotateHmacKey();
  }

  /**
   * Broadcast login event to all other tabs
   * SECURITY: Only non-sensitive user info is broadcast. Email is excluded.
   */
  broadcastLogin(user: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    roles: string[];
  }): void {
    this.logger.debug('[CrossTabAuth] Broadcasting login event', { tabId: this.tabId, userId: user.id });
    this.broadcast({
      type: 'LOGIN',
      timestamp: Date.now(),
      tabId: this.tabId,
      payload: { user }
    });
  }

  /**
   * Broadcast token refresh event to all other tabs
   * SECURITY: Does NOT include the actual token. Other tabs should fetch
   * fresh credentials from secure storage (localStorage/sessionStorage).
   */
  broadcastTokenRefresh(): void {
    this.logger.debug('[CrossTabAuth] Broadcasting token refresh event', { tabId: this.tabId });
    this.broadcast({
      type: 'TOKEN_REFRESHED',
      timestamp: Date.now(),
      tabId: this.tabId
      // No payload - other tabs fetch from secure storage
    });
  }

  /**
   * Broadcast session extended event to all other tabs
   */
  broadcastSessionExtended(): void {
    this.logger.debug('[CrossTabAuth] Broadcasting session extended event', { tabId: this.tabId });
    this.broadcast({
      type: 'SESSION_EXTENDED',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Broadcast session expired event to all other tabs
   */
  broadcastSessionExpired(): void {
    this.logger.debug('[CrossTabAuth] Broadcasting session expired event', { tabId: this.tabId });
    this.broadcast({
      type: 'SESSION_EXPIRED',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Broadcast tenant switch event to all other tabs
   * DEV-200: Cross-tab tenant synchronization
   */
  broadcastTenantSwitch(tenant: { id: string; name: string; code: string }): void {
    this.logger.debug('[CrossTabAuth] Broadcasting tenant switch event', {
      tabId: this.tabId,
      tenantId: tenant.id,
      tenantName: tenant.name
    });
    this.broadcast({
      type: 'TENANT_SWITCHED',
      timestamp: Date.now(),
      tabId: this.tabId,
      payload: { tenant }
    });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.logger.debug('[CrossTabAuth] Destroying service', { tabId: this.tabId });

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.logoutSubject.complete();
    this.loginSubject.complete();
    this.tokenRefreshSubject.complete();
    this.sessionExtendedSubject.complete();
    this.sessionExpiredSubject.complete();
    this.tenantSwitchSubject.complete();
  }

  /**
   * Initialize BroadcastChannel for modern browsers
   */
  private initializeBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel(this.CHANNEL_NAME);

      this.channel.onmessage = (event: MessageEvent<CrossTabAuthMessage>) => {
        // Handle async message processing
        this.handleMessage(event.data).catch(error => {
          this.logger.error('[CrossTabAuth] Error handling message', { error });
        });
      };

      // Note: BroadcastChannel doesn't have onerror in standard API
      // Errors are caught in the try-catch block

      this.logger.debug('[CrossTabAuth] BroadcastChannel initialized');
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to initialize BroadcastChannel', { error });
      // Fallback to localStorage
      this.initializeLocalStorageFallback();
    }
  }

  /**
   * Initialize localStorage event listener for fallback
   */
  private initializeLocalStorageFallback(): void {
    // Listen for storage events (fired when localStorage changes in other tabs)
    const storage$ = fromEvent<StorageEvent>(window, 'storage').pipe(
      filter(event => event.key === this.STORAGE_KEY && event.newValue !== null),
      map(event => {
        try {
          return JSON.parse(event.newValue!) as CrossTabAuthMessage;
        } catch (error) {
          this.logger.error('[CrossTabAuth] Failed to parse localStorage message', { error });
          return null;
        }
      }),
      filter((msg): msg is CrossTabAuthMessage => msg !== null),
      // IMPORTANT: Automatic cleanup when service is destroyed
      takeUntilDestroyed(this.destroyRef)
    );

    storage$.subscribe(message => {
      // Handle async message processing
      this.handleMessage(message).catch(error => {
        this.logger.error('[CrossTabAuth] Error handling localStorage message', { error });
      });
    });

    this.logger.debug('[CrossTabAuth] localStorage fallback initialized');
  }

  /**
   * Setup tab focus listener for state synchronization
   */
  private setupFocusListener(): void {
    // LOW-002: Use named constant instead of magic number
    const focus$ = fromEvent(window, 'focus').pipe(
      debounceTime(CROSS_TAB_CONFIG.FOCUS_DEBOUNCE_MS),
      // IMPORTANT: Automatic cleanup when service is destroyed
      takeUntilDestroyed(this.destroyRef)
    );

    focus$.subscribe(() => {
      this.logger.debug('[CrossTabAuth] Tab gained focus, checking for state changes');
      // When tab gains focus, consumers can check if they need to sync state
      // This is handled by subscribing to the relevant observables in the auth service
    });
  }

  /**
   * Broadcast a message to all other tabs
   * Signs the message with HMAC for integrity verification
   */
  private async broadcast(message: Omit<CrossTabAuthMessage, 'signature'>): Promise<void> {
    try {
      // Sign the message
      const signature = await this.signMessage(message);
      const signedMessage: CrossTabAuthMessage = { ...message, signature };

      if (this.channel) {
        // Use BroadcastChannel
        this.channel.postMessage(signedMessage);
      } else {
        // Use localStorage fallback. GAP-FE-C013: routed through the SSR-safe
        // wrapper; preserves STORAGE_KEY and the cross-tab `storage`-event signal
        // (setItem still hits localStorage, so other tabs' listeners fire as before).
        this.appStorage.setJSON(this.STORAGE_KEY, signedMessage);
        // Clear immediately to allow repeated messages
        // LOW-002: Use named constant instead of magic number
        setTimeout(() => {
          this.appStorage.removeItem(this.STORAGE_KEY);
        }, CROSS_TAB_CONFIG.MESSAGE_CLEANUP_DELAY_MS);
      }

      this.logger.debug('[CrossTabAuth] Message broadcasted', { type: message.type, tabId: message.tabId });
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to broadcast message', { error, type: message.type });
    }
  }

  /**
   * Handle incoming message from other tabs
   * Verifies HMAC signature before processing
   */
  private async handleMessage(message: CrossTabAuthMessage): Promise<void> {
    // Ignore messages from this tab
    if (message.tabId === this.tabId) {
      return;
    }

    // Validate message structure
    if (!message.type || !message.timestamp || !message.tabId) {
      this.logger.warn('[CrossTabAuth] Invalid message received', { message });
      return;
    }

    // Verify HMAC signature
    const isValid = await this.verifySignature(message);
    if (!isValid) {
      this.logger.error('[CrossTabAuth] Message signature verification failed - rejecting message', {
        type: message.type,
        fromTabId: message.tabId
      });
      return;
    }

    // Ignore old messages (older than MESSAGE_MAX_AGE_MS)
    // LOW-002: Use named constant instead of magic number
    const age = Date.now() - message.timestamp;
    if (age > CROSS_TAB_CONFIG.MESSAGE_MAX_AGE_MS) {
      this.logger.debug('[CrossTabAuth] Ignoring old message', { type: message.type, age });
      return;
    }

    this.logger.debug('[CrossTabAuth] Message received from other tab', {
      type: message.type,
      fromTabId: message.tabId,
      thisTabId: this.tabId,
      signatureVerified: true
    });

    // Route message to appropriate subject
    switch (message.type) {
      case 'LOGOUT':
        this.logoutSubject.next(message);
        break;
      case 'LOGIN':
        this.loginSubject.next(message);
        break;
      case 'TOKEN_REFRESHED':
        this.tokenRefreshSubject.next(message);
        break;
      case 'SESSION_EXTENDED':
        this.sessionExtendedSubject.next(message);
        break;
      case 'SESSION_EXPIRED':
        this.sessionExpiredSubject.next(message);
        break;
      case 'TENANT_SWITCHED':
        this.tenantSwitchSubject.next(message);
        break;
      default:
        this.logger.warn('[CrossTabAuth] Unknown message type', { type: message.type });
    }
  }

  /**
   * Generate a unique tab identifier
   */
  private generateTabId(): string {
    // Try crypto.randomUUID() first (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback to timestamp + random
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Initialize or retrieve the HMAC key for message signing
   * Key is shared across tabs via localStorage
   */
  private async initializeHmacKey(): Promise<void> {
    try {
      // Check if key exists in localStorage
      // GAP-FE-C013: SSR-safe wrapper; preserves the HMAC_KEY_STORAGE key.
      const storedKey = this.appStorage.getItem(this.HMAC_KEY_STORAGE);

      if (storedKey) {
        // Import existing key
        const keyData = this.base64ToArrayBuffer(storedKey);
        this.hmacKey = await crypto.subtle.importKey(
          'raw',
          keyData,
          { name: 'HMAC', hash: 'SHA-256' },
          false,
          ['sign', 'verify']
        );
        this.logger.debug('[CrossTabAuth] HMAC key loaded from storage');
      } else {
        // Generate new key
        this.hmacKey = await crypto.subtle.generateKey(
          { name: 'HMAC', hash: 'SHA-256' },
          true,
          ['sign', 'verify']
        );

        // Export and store for other tabs
        const exportedKey = await crypto.subtle.exportKey('raw', this.hmacKey);
        // GAP-FE-C013: SSR-safe wrapper; preserves the HMAC_KEY_STORAGE key.
        this.appStorage.setItem(this.HMAC_KEY_STORAGE, this.arrayBufferToBase64(exportedKey));
        this.logger.debug('[CrossTabAuth] New HMAC key generated and stored');
      }
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to initialize HMAC key', { error });
      // Continue without HMAC - messages will still work but won't be verified
    }
  }

  /**
   * Rotate the HMAC key (generate a new one)
   * Should be called on logout to invalidate any old messages
   */
  private async rotateHmacKey(): Promise<void> {
    try {
      // Generate new key
      this.hmacKey = await crypto.subtle.generateKey(
        { name: 'HMAC', hash: 'SHA-256' },
        true,
        ['sign', 'verify']
      );

      // Export and store for other tabs
      const exportedKey = await crypto.subtle.exportKey('raw', this.hmacKey);
      // GAP-FE-C013: SSR-safe wrapper; preserves the HMAC_KEY_STORAGE key.
      this.appStorage.setItem(this.HMAC_KEY_STORAGE, this.arrayBufferToBase64(exportedKey));

      this.logger.debug('[CrossTabAuth] HMAC key rotated');
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to rotate HMAC key', { error });
    }
  }

  /**
   * Sign a message with HMAC-SHA256
   */
  private async signMessage(message: Omit<CrossTabAuthMessage, 'signature'>): Promise<string> {
    if (!this.hmacKey) {
      return '';
    }

    try {
      const messageString = JSON.stringify({
        type: message.type,
        timestamp: message.timestamp,
        tabId: message.tabId,
        payload: message.payload
      });

      const encoder = new TextEncoder();
      const data = encoder.encode(messageString);
      const signature = await crypto.subtle.sign('HMAC', this.hmacKey, data);

      return this.arrayBufferToBase64(signature);
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to sign message', { error });
      return '';
    }
  }

  /**
   * Verify message signature
   * CRITICAL-002: Reject unsigned messages when HMAC is available
   */
  private async verifySignature(message: CrossTabAuthMessage): Promise<boolean> {
    // SECURITY: If HMAC key is not available (crypto not supported), log and allow
    // This is a fallback for browsers without Web Crypto API
    if (!this.hmacKey) {
      this.logger.warn('[CrossTabAuth] HMAC key not available - signature verification skipped');
      return true;
    }

    // CRITICAL-002: REJECT unsigned messages when HMAC is available
    // This prevents attackers from crafting malicious unsigned messages
    if (!message.signature) {
      this.logger.error('[CrossTabAuth] Rejecting unsigned message - HMAC is available but message has no signature', {
        type: message.type,
        fromTabId: message.tabId
      });
      return false;
    }

    try {
      const messageString = JSON.stringify({
        type: message.type,
        timestamp: message.timestamp,
        tabId: message.tabId,
        payload: message.payload
      });

      const encoder = new TextEncoder();
      const data = encoder.encode(messageString);
      const signature = this.base64ToArrayBuffer(message.signature);

      return await crypto.subtle.verify('HMAC', this.hmacKey, signature, data);
    } catch (error) {
      this.logger.error('[CrossTabAuth] Failed to verify signature', { error });
      return false;
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
